import { Capacitor } from "@capacitor/core";

type StraivAudioMessage =
  | { prepare: true }
  | { prepareMixPlayback: true }
  | { beginDucking: true }
  | { endDucking: true }
  | { playDuckedAudio: true; base64: string; mime?: string }
  | { active: boolean; title?: string }
  | { prepareBackgroundKeepAlive: true }
  | { beginCoachBackgroundTask: true }
  | { endCoachBackgroundTask: true }
  | { resumeMusic: true };

let pendingAudioResolve: (() => void) | null = null;
let pendingAudioTimer: number | null = null;

declare global {
  interface Window {
    __straivAudioDone?: () => void;
  }
}

if (typeof window !== "undefined") {
  window.__straivAudioDone = () => {
    if (pendingAudioTimer != null) {
      window.clearTimeout(pendingAudioTimer);
      pendingAudioTimer = null;
    }
    pendingAudioResolve?.();
    pendingAudioResolve = null;
  };
}

function postNative(message: StraivAudioMessage): void {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== "ios") return;
  try {
    const w = window as Window & {
      webkit?: {
        messageHandlers?: { straivNowPlaying?: { postMessage: (m: StraivAudioMessage) => void } };
      };
    };
    w.webkit?.messageHandlers?.straivNowPlaying?.postMessage(message);
  } catch {
    // ignore
  }
}

/** Ambient + mixWithOthers before countdown/TTS — never steals music. */
export const prepareAudioForRun = () => {
  postNative({ prepare: true });
};

/** playback + mixWithOthers only. Call before the first coach clip so duckOthers can lower music without pausing it. */
export const prepareMixPlayback = () => {
  postNative({ prepareMixPlayback: true });
};

/** Lower other apps' volume while cloud coach audio plays (duckOthers + mixWithOthers). */
export const beginAudioDucking = () => {
  postNative({ beginDucking: true });
};

/** Restore music volume after coach audio finishes (session stays active — no pause/resume). */
export const endAudioDucking = () => {
  postNative({ endDucking: true });
};

/**
 * Play coach audio natively with ducking (music stays playing quieter).
 * Avoids WKWebView HTMLAudioElement which can pause Spotify/Apple Music.
 */
export function playDuckedNativeAudio(base64: string, mime = "audio/mpeg"): Promise<void> {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== "ios") {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    if (pendingAudioTimer != null) {
      window.clearTimeout(pendingAudioTimer);
      pendingAudioTimer = null;
    }
    pendingAudioResolve = resolve;
    postNative({ playDuckedAudio: true, base64, mime });
    // Long intros need ducking for the full clip (25s used to unduck mid-message).
    pendingAudioTimer = window.setTimeout(() => {
      if (pendingAudioResolve === resolve) {
        pendingAudioTimer = null;
        pendingAudioResolve = null;
        endAudioDucking();
        resolve();
      }
    }, 180_000);
  });
}

export const setStraivNowPlaying = (active: boolean, title?: string) => {
  postNative({ active, title });
};

/** After coach audio: notify Spotify/Music to resume if they paused on duck. */
export const resumeMusicAfterRunStart = () => {
  postNative({ resumeMusic: true });
};

export const prepareBackgroundKeepAlive = () => {
  if (Capacitor.getPlatform() !== "ios") return;
  postNative({ prepareBackgroundKeepAlive: true });
};

/** Extra iOS background time while cloud TTS is fetched (phone locked / screen off). */
export const beginCoachBackgroundTask = () => {
  if (Capacitor.getPlatform() !== "ios") return;
  postNative({ beginCoachBackgroundTask: true });
};

export const endCoachBackgroundTask = () => {
  if (Capacitor.getPlatform() !== "ios") return;
  postNative({ endCoachBackgroundTask: true });
};
